import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from './ui/sheet';
import { MapPin, Navigation, Car, Clock, Users, Map, List, Plus, Layers, ChevronDown, Settings, Share2, Copy, Check, Globe, CreditCard, Flag } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import MapView from './MapView';
import AddLocationForm from './AddLocationForm';
import AddDestinationForm from './AddDestinationForm';
import LocationDetailModal from './LocationDetailModal';
import ReservationModal from './ReservationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getBarColor = (pct) => pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';
const getBarTextColor = (pct) => pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-yellow-600' : 'text-green-600';

const ConsumerView = ({
  consumerData,
  consumerJsonInput,
  setConsumerJsonInput,
  handleImportConsumerJSON,
  fakeLocations,
  onAddFakeLocation,
  onRemoveFakeLocation,
  onUpdateFakeLocation,
  onClearFakeLocations,
  branding,
  projectSlug,
  onBrandingChange,
  token,
  destinations,
  onAddDestination,
  onRemoveDestination,
  onUpdateDestination,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [activeView, setActiveView] = useState('map');
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [publishSlug, setPublishSlug] = useState(projectSlug || '');
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [reservationGarage, setReservationGarage] = useState(null);
  const { toast } = useToast();

  const findUserLocation = () => {
    if (!("geolocation" in navigator)) {
      toast({ title: "Location Not Available", description: "Your browser doesn't support location services", variant: "destructive" });
      return;
    }
    setLocationStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocationStatus('success');
        toast({ title: "Location Found!", description: "Your location has been added to the map" });
      },
      (error) => {
        setLocationStatus('error');
        const msgs = { 1: "Location access denied.", 2: "Location unavailable.", 3: "Request timed out." };
        toast({ title: "Location Error", description: msgs[error.code] || "Unknown error", variant: "destructive" });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
    );
  };

  const liveGarages = useMemo(() => {
    if (!consumerData?.data?.sites?.nodes) return [];
    return consumerData.data.sites.nodes
      .filter(site => site.address1)
      .map(site => {
        const usage = site.latestSiteParkingUsages.nodes[0];
        const utilizedPercent = parseFloat(usage.utilizedPercent) * 100;
        return {
          id: site.id, name: site.displayName, address: site.address1,
          directions: site.directions, totalSpaces: usage.totalCount,
          occupiedSpaces: usage.occupiedCount, availableSpaces: usage.availableCount,
          utilizedPercent: Math.round(utilizedPercent),
          availabilityStatus: utilizedPercent >= 90 ? 'full' : utilizedPercent >= 70 ? 'limited' : 'available',
          lastUpdated: new Date(usage.timestamp), isLive: true, isFake: false,
        };
      });
  }, [consumerData]);

  const fakeGarages = useMemo(() => {
    return fakeLocations.map(site => {
      const usage = site.latestSiteParkingUsages.nodes[0];
      const utilizedPercent = parseFloat(usage.utilizedPercent) * 100;
      return {
        id: site.id, name: site.displayName, address: site.address1,
        directions: site.directions || '', totalSpaces: usage.totalCount,
        occupiedSpaces: usage.occupiedCount, availableSpaces: usage.availableCount,
        utilizedPercent: Math.round(utilizedPercent),
        availabilityStatus: utilizedPercent >= 90 ? 'full' : utilizedPercent >= 70 ? 'limited' : 'available',
        lastUpdated: new Date(usage.timestamp), isLive: false, isFake: true,
        coordinates: site.coordinates, iconColor: site.iconColor,
        iconStyle: site.iconStyle, iconLabel: site.iconLabel,
        locationType: site.locationType, levels: site.levels || [],
        attributes: site.attributes || [], tags: site.tags || [],
        photoUrl: site.photoUrl,
      };
    });
  }, [fakeLocations]);

  const allGarages = useMemo(() => [...liveGarages, ...fakeGarages], [liveGarages, fakeGarages]);

  const filteredGarages = useMemo(() => {
    if (!searchTerm) return allGarages;
    return allGarages.filter(g =>
      g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allGarages, searchTerm]);

  const getDirectionsUrl = (garage) => {
    const coords = garage.coordinates;
    if (coords) {
      const lat = Array.isArray(coords) ? coords[0] : coords.lat;
      const lng = Array.isArray(coords) ? coords[1] : coords.lng;
      if (lat && lng) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(garage.address || '')}`;
  };

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'limited': return 'bg-yellow-100 text-yellow-800';
      case 'full': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  const getAvailabilityText = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'limited': return 'Limited';
      case 'full': return 'Full';
      default: return 'Unknown';
    }
  };

  const hasAnyData = consumerData || fakeLocations.length > 0;
  const primaryColor = branding?.primaryColor || '#3b82f6';
  const accentColor = branding?.accentColor || '#f97316';
  const textMode = branding?.textMode || 'light';
  const showReservation = branding?.showReservationDemo !== false;

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const handlePublish = async () => {
    if (!publishSlug.trim()) {
      toast({ title: "Slug required", description: "Enter a URL slug like 'purdue-university'", variant: "destructive" });
      return;
    }
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/maps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug: publishSlug, branding, locations: fakeLocations, destinations: destinations || [] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to publish');
      const url = `${window.location.origin}/view/${data.slug}`;
      setPublishedUrl(url);
      toast({ title: "Published!", description: `Your map is live at /view/${data.slug}` });
    } catch (e) {
      toast({ title: "Publish Failed", description: e.message, variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(publishedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Admin panel content as JSX variable (NOT a component — avoids remount on keystroke)
  const adminPanelContent = (
    <div className="space-y-6">
      {/* Publish & Share */}
      <div className="space-y-3 pb-5 border-b border-slate-200">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center space-x-1.5">
          <Share2 className="w-4 h-4" style={{ color: primaryColor }} />
          <span>Publish & Share</span>
        </h4>
        <p className="text-xs text-slate-500">Create a shareable link with your branding and locations.</p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">URL Slug</label>
          <div className="flex items-center space-x-1.5">
            <span className="text-xs text-slate-400 whitespace-nowrap">/view/</span>
            <Input
              type="text" value={publishSlug}
              onChange={(e) => setPublishSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="purdue-university" className="flex-1 text-sm"
              data-testid="publish-slug-input"
            />
          </div>
        </div>
        <Button onClick={handlePublish} disabled={publishing} className="w-full text-white text-sm" size="sm" style={{ backgroundColor: primaryColor }} data-testid="publish-btn">
          <Globe className="w-3.5 h-3.5 mr-1.5" />{publishing ? 'Publishing...' : 'Publish Map'}
        </Button>
        {publishedUrl && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid="published-url-box">
            <p className="text-xs text-green-700 font-medium">Map is live!</p>
            <div className="flex items-center space-x-2">
              <input type="text" readOnly value={publishedUrl} className="flex-1 text-xs bg-white border border-green-200 rounded px-2 py-1.5 text-slate-700" />
              <button onClick={handleCopyUrl} className="p-1.5 rounded-md bg-green-100 hover:bg-green-200 transition-colors" data-testid="copy-url-btn">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-green-600" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Demo Features */}
      <div className="space-y-3 pb-5 border-b border-slate-200">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center space-x-1.5">
          <CreditCard className="w-4 h-4" style={{ color: primaryColor }} />
          <span>Demo Features</span>
        </h4>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Reservation Demo</p>
            <p className="text-xs text-slate-500">Show "Pay For Reserved Parking" buttons</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={branding?.showReservationDemo !== false}
            onClick={() => {
              const updated = { ...branding, showReservationDemo: branding?.showReservationDemo === false };
              onBrandingChange(updated);
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${branding?.showReservationDemo !== false ? '' : 'bg-slate-300'}`}
            style={branding?.showReservationDemo !== false ? { backgroundColor: primaryColor } : {}}
            data-testid="reservation-demo-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${branding?.showReservationDemo !== false ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Show Destinations</p>
            <p className="text-xs text-slate-500">Display key destinations & walk times on public map</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={branding?.showDestinations !== false}
            onClick={() => {
              const updated = { ...branding, showDestinations: branding?.showDestinations === false };
              onBrandingChange(updated);
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${branding?.showDestinations !== false ? '' : 'bg-slate-300'}`}
            style={branding?.showDestinations !== false ? { backgroundColor: primaryColor } : {}}
            data-testid="destinations-visibility-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${branding?.showDestinations !== false ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Custom Locations</h4>
        <AddLocationForm
          fakeLocations={fakeLocations}
          onAddLocation={(loc) => { onAddFakeLocation(loc); }}
          onRemoveLocation={onRemoveFakeLocation}
          onUpdateLocation={onUpdateFakeLocation}
          onClearAll={onClearFakeLocations}
          accentColor={primaryColor}
        />
      </div>

      {/* Destinations */}
      <div className="border-t border-slate-200 pt-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center space-x-1.5">
          <Flag className="w-4 h-4" style={{ color: primaryColor }} />
          <span>Key Destinations</span>
        </h4>
        <p className="text-xs text-slate-500 mb-3">Places parkers walk to. Walk times are shown on the public map.</p>
        <AddDestinationForm
          destinations={destinations || []}
          onAddDestination={onAddDestination}
          onRemoveDestination={onRemoveDestination}
          onUpdateDestination={onUpdateDestination}
          accentColor={primaryColor}
        />
      </div>
    </div>
  );

  // No-data state
  if (!hasAnyData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-2" data-testid="find-parking-heading">
            {branding?.organizationName ? `${branding.organizationName} - Find Parking` : 'Find Parking'}
          </h2>
          <p className="text-slate-600">Load live data or add custom parking locations</p>
        </div>
        <div className="flex justify-center">
          <Card className="bg-white border-slate-200 shadow-lg max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <Plus className="w-5 h-5 text-green-600" />
                <span>Get Started</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Add parking locations or connect to live data to get started.
              </p>
              <Sheet open={adminOpen} onOpenChange={setAdminOpen}>
                <SheetTrigger asChild>
                  <Button className="w-full text-white" style={{ backgroundColor: primaryColor }} data-testid="open-admin-panel-btn">
                    <Settings className="w-4 h-4 mr-2" />Manage Locations
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[90vw] sm:w-[420px] md:w-[460px] overflow-y-auto pl-8 pr-10 pt-8 pb-6">
                  <SheetHeader className="mb-4">
                    <SheetTitle className="flex items-center space-x-2">
                      <Settings className="w-5 h-5" style={{ color: primaryColor }} />
                      <span>Manage Locations</span>
                    </SheetTitle>
                    <SheetDescription>Add, edit, or remove parking locations and load live data.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-4">
                    {adminPanelContent}
                  </div>
                </SheetContent>
              </Sheet>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Data loaded state — full-width layout, admin behind gear icon
  return (
    <div className="space-y-6">
      {/* Branded header with search + admin gear */}
      <div className="rounded-2xl shadow-lg p-4 sm:p-6" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.85)})` }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            {branding?.logoUrl && (
              <img src={branding.logoUrl} alt="" className="h-14 w-auto object-contain" />
            )}
            <div>
              <h2 className={`text-xl sm:text-2xl font-bold ${textMode === 'light' ? 'text-white' : 'text-slate-900'}`} data-testid="parking-availability-heading">
                {branding?.organizationName ? `${branding.organizationName} Parking` : 'Parking Availability'}
              </h2>
              <p className={`text-sm ${textMode === 'light' ? 'text-white/70' : 'text-slate-600'}`}>Find available parking near you</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Input
              type="text" placeholder="Search garages..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 bg-white/90 border-white/30 placeholder:text-slate-400"
              data-testid="search-garages-input"
            />
            <Sheet open={adminOpen} onOpenChange={setAdminOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className={`h-10 w-10 ${textMode === 'light' ? 'border-white/30 bg-white/10 hover:bg-white/20 text-white' : 'border-slate-300'}`} data-testid="admin-gear-btn">
                  <Settings className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[90vw] sm:w-[420px] md:w-[460px] overflow-y-auto pl-8 pr-10 pt-8 pb-6">
                <SheetHeader className="mb-4">
                  <SheetTitle className="flex items-center space-x-2">
                    <Settings className="w-5 h-5" style={{ color: primaryColor }} />
                    <span>Manage Locations</span>
                  </SheetTitle>
                  <SheetDescription>Add, edit, or remove parking locations and load live data.</SheetDescription>
                </SheetHeader>
                <div className="mt-4">
                  {adminPanelContent}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
                <Car className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: primaryColor }} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Locations</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800" data-testid="total-locations-count">{filteredGarages.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Available</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800" data-testid="available-spaces-count">
                  {filteredGarages.reduce((sum, g) => sum + g.availableSpaces, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 rounded-lg" style={{ backgroundColor: `${accentColor}20` }}>
                <Clock className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Utilization</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800" data-testid="avg-utilization">
                  {filteredGarages.length > 0
                    ? Math.round(filteredGarages.reduce((sum, g) => sum + g.utilizedPercent, 0) / filteredGarages.length)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map/List — now full width */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200">
        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <div className="p-3 sm:p-6 pb-0">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="map" className="flex items-center space-x-2" data-testid="map-view-tab">
                <Map className="w-4 h-4" /><span>Map View</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="flex items-center space-x-2" data-testid="list-view-tab">
                <List className="w-4 h-4" /><span>List View</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="map" className="p-3 sm:p-6 pt-4">
            <MapView
              garages={filteredGarages}
              onDirectionsClick={(g) => window.open(getDirectionsUrl(g), '_blank')}
              onGarageClick={(g) => setSelectedGarage(g)}
              onFindLocation={findUserLocation}
              locationStatus={locationStatus}
              userLocation={userLocation}
              mapCenter={branding?.mapCenter}
              mapZoom={branding?.mapZoom}
              primaryColor={primaryColor}
              onReserveClick={showReservation ? (g) => setReservationGarage(g) : null}
            />
          </TabsContent>

          <TabsContent value="list" className="p-3 sm:p-6 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {filteredGarages.map((garage) => {
                const hasLevels = garage.levels && garage.levels.length > 0;
                const hasAttrs = garage.attributes && garage.attributes.length > 0;
                const hasDetails = hasLevels || hasAttrs;

                return (
                  <Card key={garage.id} className="bg-white border-slate-200 hover:shadow-xl transition-shadow duration-300 cursor-pointer overflow-hidden" data-testid={`garage-card-${garage.id}`} onClick={() => setSelectedGarage(garage)}>
                    {/* Full-width photo */}
                    {garage.photoUrl && (
                      <img src={garage.photoUrl} alt={garage.name} className="w-full h-36 sm:h-44 object-cover" />
                    )}
                    <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base sm:text-lg font-semibold text-slate-800 mb-1">{garage.name}</CardTitle>
                          <div className="flex items-start text-xs sm:text-sm text-slate-600">
                            <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-2">{garage.address}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-1 flex-shrink-0">
                          <Badge className={`${getAvailabilityColor(garage.availabilityStatus)} font-medium text-[10px] sm:text-xs`}>
                            {getAvailabilityText(garage.availabilityStatus)}
                          </Badge>
                          {garage.locationType && (
                            <Badge className="bg-slate-100 text-slate-600 text-[10px] sm:text-xs">
                              {garage.locationType === 'garage' ? 'Garage' : 'Surface'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-2.5 sm:space-y-3 px-3 sm:px-6 pb-3 sm:pb-6">
                      {/* Overall Stats + Progress Bar */}
                      <div className="bg-slate-50 rounded-lg p-3 sm:p-4">
                        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
                          <div>
                            <div className="text-lg sm:text-xl font-bold text-green-600">{garage.availableSpaces}</div>
                            <div className="text-[10px] sm:text-xs text-slate-500">Available</div>
                          </div>
                          <div>
                            <div className="text-lg sm:text-xl font-bold text-slate-800">{garage.totalSpaces}</div>
                            <div className="text-[10px] sm:text-xs text-slate-500">Total</div>
                          </div>
                          <div>
                            <div className={`text-lg sm:text-xl font-bold ${getBarTextColor(garage.utilizedPercent)}`}>
                              {garage.utilizedPercent}%
                            </div>
                            <div className="text-[10px] sm:text-xs text-slate-500">Utilized</div>
                          </div>
                        </div>
                        <div className="mt-2 sm:mt-3 w-full bg-slate-200 rounded-full h-2 sm:h-2.5" data-testid={`progress-bar-bg-${garage.id}`}>
                          <div
                            className={`h-2 sm:h-2.5 rounded-full transition-all ${getBarColor(garage.utilizedPercent)}`}
                            style={{ width: `${Math.max(garage.utilizedPercent, 2)}%` }}
                            data-testid={`progress-bar-fill-${garage.id}`}
                          />
                        </div>
                      </div>

                      {/* Accordion for Levels & Attributes */}
                      {hasDetails && (
                        <details className="group" onClick={(e) => e.stopPropagation()} data-testid={`expand-details-btn-${garage.id}`}>
                          <summary className="flex items-center justify-between w-full text-sm text-slate-600 hover:text-slate-800 py-1.5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                            <span className="flex items-center space-x-1.5">
                              <Layers className="w-3.5 h-3.5" />
                              <span>
                                {hasLevels ? `${garage.levels.length} levels` : ''}
                                {hasLevels && hasAttrs ? ' · ' : ''}
                                {hasAttrs ? `${garage.attributes.length} types` : ''}
                              </span>
                            </span>
                            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                          </summary>
                          <div className="space-y-3 mt-2 pb-1" data-testid={`expanded-details-${garage.id}`}>
                            {hasLevels && (
                              <div className="space-y-1.5">
                                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">By Level</div>
                                {garage.levels.map((level, i) => (
                                  <div key={i} className="space-y-0.5">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-600 font-medium">{level.name}</span>
                                      <span className={`font-bold ${getBarTextColor(level.utilizedPercent)}`}>
                                        {level.availableSpaces}/{level.totalSpaces}
                                      </span>
                                    </div>
                                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                                      <div className={`h-1.5 rounded-full ${getBarColor(level.utilizedPercent)}`} style={{ width: `${Math.max(level.utilizedPercent, 2)}%` }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {hasAttrs && (
                              <div className="space-y-1.5">
                                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">By Type</div>
                                {garage.attributes.map((attr, i) => (
                                  <div key={i} className="space-y-0.5">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-600 font-medium">{attr.name}</span>
                                      <span className={`font-bold ${getBarTextColor(attr.utilizedPercent)}`}>
                                        {attr.availableSpaces}/{attr.totalSpaces}
                                      </span>
                                    </div>
                                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                                      <div className={`h-1.5 rounded-full ${getBarColor(attr.utilizedPercent)}`} style={{ width: `${Math.max(attr.utilizedPercent, 2)}%` }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </details>
                      )}

                      {/* Tags */}
                      {garage.tags && garage.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {garage.tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs">{tag}</span>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      {showReservation && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); setReservationGarage(garage); }}
                          variant="outline"
                          className="w-full text-sm flex items-center justify-center space-x-1 border-2 font-semibold"
                          style={{ borderColor: primaryColor, color: primaryColor }}
                          data-testid={`reserve-btn-${garage.id}`}
                        >
                          <CreditCard className="w-3.5 h-3.5" /><span>Pay For Reserved Parking</span>
                        </Button>
                      )}
                      <Button
                        onClick={(e) => { e.stopPropagation(); window.open(getDirectionsUrl(garage), '_blank'); }}
                        className="w-full text-white text-sm flex items-center justify-center space-x-1"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Navigation className="w-3.5 h-3.5" /><span>Directions</span>
                      </Button>

                      <div className="text-xs text-slate-500 text-center">
                        Updated: {garage.lastUpdated.toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredGarages.length === 0 && (
              <div className="text-center py-12">
                <Car className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">No parking locations found</h3>
                <p className="text-slate-500">Try adjusting your search terms or add custom locations.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Location Detail Modal */}
      <LocationDetailModal
        garage={selectedGarage}
        open={!!selectedGarage}
        onClose={() => setSelectedGarage(null)}
        primaryColor={primaryColor}
      />
      <ReservationModal
        garage={reservationGarage}
        open={!!reservationGarage}
        onClose={() => setReservationGarage(null)}
        primaryColor={primaryColor}
      />
    </div>
  );
};

export default ConsumerView;
